import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '../components/ui/dialog';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '../components/ui/alert-dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../components/ui/table';
import { toast } from '../utils/safeToast';
import { Plus, Search, Edit, Trash2, Loader2, RotateCcw, Zap, Droplets, Flame, Paintbrush, Hammer, Wrench } from 'lucide-react';

const API = "/api";

const TRADES = [
  { value: 'elektrik', label: 'Elektrik', icon: Zap, color: 'bg-yellow-100 text-yellow-700' },
  { value: 'sanitaer', label: 'Sanitär', icon: Droplets, color: 'bg-blue-100 text-blue-700' },
  { value: 'heizung', label: 'Heizung', icon: Flame, color: 'bg-orange-100 text-orange-700' },
  { value: 'maler', label: 'Maler', icon: Paintbrush, color: 'bg-purple-100 text-purple-700' },
  { value: 'tischler', label: 'Tischler', icon: Hammer, color: 'bg-amber-100 text-amber-700' },
  { value: 'allgemein', label: 'Allgemein', icon: Wrench, color: 'bg-slate-100 text-slate-700' },
];

const ContractorsPage = () => {
  const [contractors, setContractors] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterTrade, setFilterTrade] = useState('all');
  const [dialogOpen, setDialogOpen] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [selectedContractor, setSelectedContractor] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [showInactive, setShowInactive] = useState(false);
  const [formData, setFormData] = useState({
    name: '',
    trade: '',
    contact_person: '',
    contact_salutation: '',
    email: '',
    phone: '',
    notes: ''
  });

  const fetchContractors = useCallback(async () => {
    try {
      const params = { include_inactive: showInactive };
      if (filterTrade && filterTrade !== 'all') {
        params.trade = filterTrade;
      }
      const response = await axios.get(`${API}/contractors`, { params });
      setContractors(response.data);
    } catch (error) {
      toast.error('Fehler beim Laden der Handwerksfirmen');
    } finally {
      setLoading(false);
    }
  }, [showInactive, filterTrade]);

  useEffect(() => {
    fetchContractors();
  }, [fetchContractors]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formData.name.trim() || !formData.email.trim()) {
      toast.error('Bitte Firmenname und E-Mail eingeben');
      return;
    }
    
    setSubmitting(true);
    try {
      if (selectedContractor) {
        await axios.put(`${API}/contractors/${selectedContractor.id}`, formData);
        toast.success('Handwerksfirma aktualisiert');
      } else {
        await axios.post(`${API}/contractors`, formData);
        toast.success('Handwerksfirma erstellt');
      }
      setDialogOpen(false);
      resetForm();
      fetchContractors();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Fehler beim Speichern');
    } finally {
      setSubmitting(false);
    }
  };

  const handleDelete = async () => {
    try {
      await axios.delete(`${API}/contractors/${selectedContractor.id}`);
      toast.success('Handwerksfirma deaktiviert');
      setDeleteDialogOpen(false);
      setSelectedContractor(null);
      fetchContractors();
    } catch (error) {
      toast.error('Fehler beim Deaktivieren');
    }
  };

  const handleReactivate = async (contractor) => {
    try {
      await axios.put(`${API}/contractors/${contractor.id}`, { status: 'aktiv' });
      toast.success('Handwerksfirma reaktiviert');
      fetchContractors();
    } catch (error) {
      toast.error('Fehler beim Reaktivieren');
    }
  };

  const openEditDialog = (contractor) => {
    setSelectedContractor(contractor);
    setFormData({
      name: contractor.name,
      trade: contractor.trade || '',
      contact_person: contractor.contact_person || '',
      contact_salutation: contractor.contact_salutation || '',
      email: contractor.email,
      phone: contractor.phone || '',
      notes: contractor.notes || ''
    });
    setDialogOpen(true);
  };

  const resetForm = () => {
    setSelectedContractor(null);
    setFormData({
      name: '',
      trade: '',
      contact_person: '',
      contact_salutation: '',
      email: '',
      phone: '',
      notes: ''
    });
  };

  const getTradeInfo = (tradeValue) => {
    return TRADES.find(t => t.value === tradeValue) || TRADES.find(t => t.value === 'allgemein');
  };

  const filteredContractors = contractors.filter(contractor =>
    contractor.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
    contractor.email.toLowerCase().includes(searchTerm.toLowerCase()) ||
    (contractor.contact_person && contractor.contact_person.toLowerCase().includes(searchTerm.toLowerCase()))
  );

  const inactiveCount = contractors.filter(c => c.status === 'inaktiv').length;

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-slate-900"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="contractors-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-slate-900 font-['Manrope']">Handwerksfirmen</h1>
          <p className="text-stone-500 text-sm">{contractors.filter(c => c.status !== 'inaktiv').length} aktive Firmen</p>
        </div>
        <Button
          onClick={() => { resetForm(); setDialogOpen(true); }}
          className="bg-[#0E4C92] hover:bg-[#0A3A70] text-white rounded-full"
          data-testid="add-contractor-btn"
        >
          <Plus className="w-4 h-4 mr-2" />
          Neue Handwerksfirma
        </Button>
      </div>

      {/* Search and Filters */}
      <div className="flex flex-col sm:flex-row gap-4">
        <div className="relative flex-1 max-w-md">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-stone-400" />
          <Input
            placeholder="Handwerksfirmen suchen..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="pl-10 bg-white border-stone-200"
            data-testid="contractor-search"
          />
        </div>
        <Select value={filterTrade} onValueChange={setFilterTrade}>
          <SelectTrigger className="w-[180px] bg-white" data-testid="filter-trade">
            <SelectValue placeholder="Alle Gewerke" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">Alle Gewerke</SelectItem>
            {TRADES.map(trade => (
              <SelectItem key={trade.value} value={trade.value}>{trade.label}</SelectItem>
            ))}
          </SelectContent>
        </Select>
        {inactiveCount > 0 && (
          <Button
            variant={showInactive ? "default" : "outline"}
            onClick={() => setShowInactive(!showInactive)}
            className={`rounded-full ${showInactive ? 'bg-stone-600 hover:bg-stone-700' : ''}`}
            data-testid="show-inactive-btn"
          >
            Inaktive ({inactiveCount})
          </Button>
        )}
      </div>

      {/* Table */}
      <div className="bg-white border border-stone-200 rounded-lg overflow-hidden">
        <div className="overflow-x-auto">
          <Table>
            <TableHeader className="bg-stone-50">
              <TableRow>
                <TableHead className="font-semibold text-slate-900">Firma</TableHead>
                <TableHead className="font-semibold text-slate-900">Gewerk</TableHead>
                <TableHead className="font-semibold text-slate-900">Ansprechpartner</TableHead>
                <TableHead className="font-semibold text-slate-900">E-Mail</TableHead>
                <TableHead className="font-semibold text-slate-900">Telefon</TableHead>
                <TableHead className="font-semibold text-slate-900">Status</TableHead>
                <TableHead className="font-semibold text-slate-900 text-right">Aktionen</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {filteredContractors.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={7} className="text-center py-12 text-stone-500">
                    {searchTerm || filterTrade !== 'all' ? 'Keine Firmen gefunden' : 'Noch keine Handwerksfirmen angelegt'}
                  </TableCell>
                </TableRow>
              ) : (
                filteredContractors.map((contractor) => {
                  const tradeInfo = getTradeInfo(contractor.trade);
                  return (
                    <TableRow
                      key={contractor.id}
                      className={`hover:bg-stone-50 transition-colors ${contractor.status === 'inaktiv' ? 'opacity-60' : ''}`}
                      data-testid={`contractor-row-${contractor.id}`}
                    >
                      <TableCell className="font-medium text-slate-900">{contractor.name}</TableCell>
                      <TableCell>
                        {tradeInfo && (
                          <Badge className={`text-xs border-0 ${tradeInfo.color}`}>
                            {tradeInfo.label}
                          </Badge>
                        )}
                      </TableCell>
                      <TableCell className="text-stone-600">
                        {contractor.contact_salutation && `${contractor.contact_salutation} `}{contractor.contact_person || '–'}
                      </TableCell>
                      <TableCell>
                        <a href={`mailto:${contractor.email}`} className="text-[#0E4C92] hover:underline">
                          {contractor.email}
                        </a>
                      </TableCell>
                      <TableCell className="text-stone-600">{contractor.phone || '–'}</TableCell>
                      <TableCell>
                        <Badge className={`text-xs border-0 ${contractor.status === 'inaktiv' ? 'bg-stone-100 text-stone-500' : 'bg-emerald-100 text-emerald-700'}`}>
                          {contractor.status === 'inaktiv' ? 'Inaktiv' : 'Aktiv'}
                        </Badge>
                      </TableCell>
                      <TableCell className="text-right">
                        <div className="flex justify-end gap-1">
                          {contractor.status === 'inaktiv' ? (
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => handleReactivate(contractor)}
                              className="text-emerald-600 hover:text-emerald-700 hover:bg-emerald-50"
                              data-testid={`reactivate-${contractor.id}`}
                            >
                              <RotateCcw className="w-4 h-4 mr-1" />
                              Reaktivieren
                            </Button>
                          ) : (
                            <>
                              <Button
                                variant="outline"
                                size="sm"
                                onClick={() => openEditDialog(contractor)}
                                data-testid={`edit-contractor-${contractor.id}`}
                                className="text-[#0E4C92] border-[#0E4C92] hover:bg-[#0E4C92] hover:text-white"
                              >
                                <Edit className="w-4 h-4 mr-1" />
                                Bearbeiten
                              </Button>
                              <Button
                                variant="outline"
                                size="sm"
                                onClick={() => { setSelectedContractor(contractor); setDeleteDialogOpen(true); }}
                                className="text-red-600 border-red-300 hover:bg-red-600 hover:text-white hover:border-red-600"
                                data-testid={`delete-contractor-${contractor.id}`}
                              >
                                <Trash2 className="w-4 h-4 mr-1" />
                                Löschen
                              </Button>
                            </>
                          )}
                        </div>
                      </TableCell>
                    </TableRow>
                  );
                })
              )}
            </TableBody>
          </Table>
        </div>
      </div>

      {/* Create/Edit Dialog */}
      <Dialog open={dialogOpen} onOpenChange={(open) => { if (!open) resetForm(); setDialogOpen(open); }}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="font-['Manrope']">
              {selectedContractor ? 'Handwerksfirma bearbeiten' : 'Neue Handwerksfirma'}
            </DialogTitle>
          </DialogHeader>
          <form onSubmit={handleSubmit} className="space-y-4 mt-4">
            <div className="space-y-2">
              <Label htmlFor="name">Firmenname *</Label>
              <Input
                id="name"
                value={formData.name}
                onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                placeholder="Elektro Petersen"
                required
                className="bg-stone-50"
                data-testid="contractor-name-input"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="trade">Gewerk</Label>
              <Select
                value={formData.trade}
                onValueChange={(value) => setFormData({ ...formData, trade: value })}
              >
                <SelectTrigger className="bg-stone-50" data-testid="contractor-trade-select">
                  <SelectValue placeholder="Gewerk wählen" />
                </SelectTrigger>
                <SelectContent>
                  {TRADES.map(trade => (
                    <SelectItem key={trade.value} value={trade.value}>{trade.label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label htmlFor="contact_person">Ansprechpartner</Label>
              <div className="flex gap-2">
                <Select
                  value={formData.contact_salutation || 'none'}
                  onValueChange={(value) => setFormData({ ...formData, contact_salutation: value === 'none' ? '' : value })}
                >
                  <SelectTrigger className="bg-stone-50 w-24" data-testid="contractor-salutation-select">
                    <SelectValue placeholder="–" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="none">–</SelectItem>
                    <SelectItem value="Herr">Herr</SelectItem>
                    <SelectItem value="Frau">Frau</SelectItem>
                  </SelectContent>
                </Select>
                <Input
                  id="contact_person"
                  value={formData.contact_person}
                  onChange={(e) => setFormData({ ...formData, contact_person: e.target.value })}
                  placeholder="Petersen"
                  className="bg-stone-50 flex-1"
                  data-testid="contractor-contact-input"
                />
              </div>
            </div>
            <div className="space-y-2">
              <Label htmlFor="email">E-Mail *</Label>
              <Input
                id="email"
                type="email"
                value={formData.email}
                onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                placeholder="info@elektro-petersen.de"
                required
                className="bg-stone-50"
                data-testid="contractor-email-input"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="phone">Telefon</Label>
              <Input
                id="phone"
                value={formData.phone}
                onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                placeholder="+49 4651 98765"
                className="bg-stone-50"
                data-testid="contractor-phone-input"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="notes">Notizen</Label>
              <Textarea
                id="notes"
                value={formData.notes}
                onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
                placeholder="Interne Notizen..."
                className="bg-stone-50"
                data-testid="contractor-notes-input"
              />
            </div>
            <DialogFooter className="pt-4 gap-3">
              <Button type="button" variant="outline" onClick={() => setDialogOpen(false)} className="rounded-full">
                Abbrechen
              </Button>
              <Button
                type="submit"
                className="bg-[#0E4C92] hover:bg-[#0A3A70] rounded-full"
                disabled={submitting}
                data-testid="contractor-submit-btn"
              >
                {submitting && <Loader2 className="w-4 h-4 animate-spin mr-2" />}
                {selectedContractor ? 'Speichern' : 'Erstellen'}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* Delete Confirmation */}
      <AlertDialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Handwerksfirma deaktivieren?</AlertDialogTitle>
            <AlertDialogDescription>
              &quot;{selectedContractor?.name}&quot; wird deaktiviert und erscheint nicht mehr in Auswahllisten.
              Sie kann später reaktiviert werden.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel className="rounded-full">Abbrechen</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleDelete}
              className="bg-red-600 hover:bg-red-700 rounded-full"
              data-testid="confirm-delete-contractor"
            >
              Deaktivieren
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
};

export default ContractorsPage;
